import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ContactPermission, Conversation} from '../../../models/inbox';
import {CommunicationService} from '../../services/communication/communication.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AppSettings} from '../../utils/app-settings';
import {ContactPermissionTabs} from '../../../models/campaign';
import {InfluencerService} from '../../services/influencer/influencer.service';
import {ContactPermissionStatus, InfluencerInfo, RequestInfluencerType} from '../../../models/influencer';
import {UserService} from '../../services/user.service';
import {ModalService} from '../../services/modal/modal.service';
import {AchooPopupComponent} from '../../core/achoo-popup/achoo-popup.component';
import * as Sentry from '@sentry/browser';
import {
  ActionDetails, ActionSource,
  EventTrackerService, Page, TargetType,
  UserActionType
} from '../../services/event-tracker/event-tracker.service';
import { InfluencerRequestError } from '../influencer-details/fail-retrieve-campaign-modal/fail-retrieve-campaign-modal.component';
import { CampaignInfluencerStatus, CommonConstant } from '../../app.constants';
import {AuthService} from '../../core/auth.service';

@Component({
  selector: 'contact-permission',
  templateUrl: './contact-permission.component.html',
  styleUrls: ['./contact-permission.component.scss']
})
export class ContactPermissionComponent implements OnInit, OnDestroy {
  @ViewChild('WarningFailToRetrieveContactPermission')
  warningFailToRetrieveContactPermission: AchooPopupComponent;

  contactPermission: ContactPermission;
  selectedTab: ContactPermissionTabs;
  conversation: Conversation;
  showActivityLog = true;
  permissionToken: string;
  errorType: InfluencerRequestError;
  isDeletedOrError: boolean;
  private accessToken;
  constructor(private contactService: CommunicationService,
              private userService: UserService,
              private modalService: ModalService,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private influencerService: InfluencerService,
              private eventTracker: EventTrackerService,
              private auth: AuthService) { }

  ngOnInit() {
    this.hideSideBar();
    this.activatedRoute.queryParams.subscribe((qp) => {
      if (qp.permissionToken) {
        this.permissionToken = qp.permissionToken;
        this.loadContactPermission(qp.permissionToken);
      }
      if (qp.accessToken) {
        this.accessToken = qp.accessToken;
      }
    });
    this.selectedTab = ContactPermissionTabs.CONTACT_PERMISSION;
  }

  ngOnDestroy() {
    this.showSideBar();
  }

  private loadContactPermission(permissionToken) {
    return this.contactService.getPermissionByToken(permissionToken).subscribe((res) => {
      this.contactPermission = res;
      const actionDetail = this.getMPActionDetail();
      this.sendMixpanelEvent(actionDetail);
      if (this.contactPermission.status === ContactPermissionStatus.REJECTED) {
        this.isDeletedOrError = true;
        this.showErrorPopup(InfluencerRequestError.CONTACT_PERMISSION_DELETE);
      } else {
        // check whether the influncer has been confirmed their account
        this.checkInfluencerConfirmation(this.userService.getInfluencerInfo());
      }
    }, (error) => {
      // handle in case the WS PR didn't merge yet
      if (error.status === 404) {
        this.contactPermission = new ContactPermission();
      } else {
        this.isDeletedOrError = true;
        this.showErrorPopup(InfluencerRequestError.RETREIVE_CONTACT_PERMISSION_REQUEST_FAIL);
      }
    });
  }

  isActivatedAdvertiserChat() {
    return !AppSettings.isDisableChat();
  }

  private checkInfluencerConfirmation(influencer: InfluencerInfo) {
    if (influencer && !influencer.acceptedTermService) {
      this.auth.needAcceptedTermAndCondition();
    }
  }

  private showErrorPopup(errorType: InfluencerRequestError) {
    this.errorType = errorType;
    this.modalService.openInCenter(this.warningFailToRetrieveContactPermission, null, 'fail-retrieve-camp', true);
  }

  public onSelectContactPermissionTab() {
    this.selectedTab = ContactPermissionTabs.CONTACT_PERMISSION;
  }

  public onSelectContactTab() {
    this.selectedTab = ContactPermissionTabs.CONTACT;
    this.loadInfluencerConversation(this.contactPermission.advertiserId, this.contactPermission.influencerId);
  }

  private loadInfluencerConversation(campaignCompany: number, influencerId: number) {
    this.contactService.fetchInfluencerConversation(campaignCompany, influencerId).subscribe((res) => {
      this.conversation = res;
    });
  }

  public isSelectedPermissionTab() {
    return this.selectedTab && this.selectedTab === ContactPermissionTabs.CONTACT_PERMISSION;
  }

  public isSelectedContactTab() {
    return this.selectedTab && this.selectedTab === ContactPermissionTabs.CONTACT;
  }

  onDeclineContactPermissionRequest(evt) {
    this.goToAllSetOrRejectPage(false, null, true);
    this.influencerService.rejectContactPermission(evt).subscribe((res) => {
      this.sendMixpanelEvent(ActionDetails.INFLUENCER_REJECTED_REQUEST);
    }, (error) => {
      this.goToAllSetOrRejectPage(false, null, false);
    });
  }

  onAcceptContactPermissionRequest(evt) {
    this.goToAllSetOrRejectPage(true, true);
    this.influencerService.acceptContactPermission(evt).subscribe((res) => {
      this.sendMixpanelEvent(ActionDetails.INFLUENCER_ACCEPTED_REQUEST);
    }, (error) => {
      Sentry.captureException(error);
      return this.goToAllSetOrRejectPage(true, false);
    });
  }

  requestNewPermission() {
    const currentInfluencer = this.userService.getInfluencerInfo();
    if (currentInfluencer) {
      this.influencerService.requestNewCampaignInvitation(currentInfluencer.id, this.contactPermission.id, RequestInfluencerType.CONTACT_PERMISSION)
        .subscribe(res => {
          this.modalService.close();
          this.router.navigate(['dashboard', 'inbox']);
        }, (err) => {
          this.modalService.close();
          this.router.navigate(['dashboard', 'inbox']);
        });
    }
  }

  private hideSideBar() {
    document.body.classList.add('no-sidebar');
  }

  private showSideBar() {
    document.body.classList.remove('no-sidebar');
  }

  private goToAllSetOrRejectPage(isAcceptOrRejectContactPermission: boolean, acceptionResult?, declineResult?) {
    let queryParams;
    if (isAcceptOrRejectContactPermission) {
      queryParams = {
        accessToken: this.accessToken,
        permissionToken: this.permissionToken,
        requestType: 'contact_permission',
        acceptionResult,
        influencerName: this.contactPermission.influencerName,
        company: this.contactPermission.agencyName,
        advertiserName: this.contactPermission.advertiserName,
        permissionId: this.contactPermission.id,
        advertiserId: this.contactPermission.advertiserId
      };
    } else {
      queryParams = {
        accessToken: this.accessToken,
        permissionToken: this.permissionToken,
        requestType: 'contact_permission',
        declineResult,
        influencerName: this.contactPermission.influencerName,
        company: this.contactPermission.agencyName,
        advertiserName: this.contactPermission.advertiserName,
        permissionId: this.contactPermission.id,
        advertiserId: this.contactPermission.advertiserId
      };
    }
    return this.router.navigate(['user', 'all-set'], {
      queryParams
    });
  }

  private sendMixpanelEvent(actionDetails, isAcceptOrRejectRequest?) {
    if (!isAcceptOrRejectRequest) {
      this.eventTracker.trackUserAction(UserActionType.VIEW, Page.CONTACT_PERMISSION, {
        type: TargetType.ACCOUNT,
        id: this.contactPermission.advertiserId,
        name: this.contactPermission.agencyName
      }, {
        actionDetails: actionDetails,
        source: this.isLandingPageMode ? ActionSource.CAMPAIGN_LANDING_PAGE : ActionSource.INFLUENCER_APP,
        permissionId: this.contactPermission.id
      }, undefined, null);
    } else {
      this.eventTracker.trackUserAction(UserActionType.ACTION, Page.CONTACT_PERMISSION,
        {
          type: TargetType.ACCOUNT,
          id: this.contactPermission.advertiserId,
          name: this.contactPermission.agencyName
        }, {
        actionDetails: actionDetails,
        source: this.isLandingPageMode ? ActionSource.CAMPAIGN_LANDING_PAGE : ActionSource.INFLUENCER_APP,
        advertiserId: this.contactPermission.advertiserId,
        action: UserActionType.UPDATE
      }, undefined, null);
    }

  }

  get isLandingPageMode() {
    return this.accessToken;
  }

  isShowRetryButton(errorType: InfluencerRequestError) {
    return errorType === InfluencerRequestError.RETREIVE_CONTACT_PERMISSION_REQUEST_FAIL;
  }

  tryAgainHandler(errorType) {
    if (!errorType) {
      return null;
    }
    if (errorType === InfluencerRequestError.CAMPAIGN_INVITATION_DELETE) {
      return this.requestNewPermission();
    } else {
      return this.reloadPlatform();
    }
  }

  private reloadPlatform() {
    this.modalService.close();
    return document.location.reload();
  }

  private getMPActionDetail() {
    let actionDetail;
    if (this.contactPermission.status.toLowerCase() === CommonConstant.CAMPAIGN_INFLUENCER_STATUS.INVITED.name.toLowerCase()) {
      actionDetail = ActionDetails.INFLUENCER_INVITE_REQUEST;
    } else if (this.contactPermission.status.toLowerCase() === CommonConstant.CAMPAIGN_INFLUENCER_STATUS.ACCEPTED.name.toLowerCase()) {
      actionDetail = ActionDetails.INFLUENCER_ACCEPTED_REQUEST;
    } else if (this.contactPermission.status.toLowerCase() === CommonConstant.CAMPAIGN_INFLUENCER_STATUS.DECLINED.name.toLowerCase()) {
      actionDetail = ActionDetails.INFLUENCER_DECLINED_REQUEST;
    }
    return actionDetail;
  }
}



// WEBPACK FOOTER //
// ./src/app/influencer-platform/contact-permission/contact-permission.component.ts